import { ArrowTrendingDownIcon, ArrowTrendingUpIcon, ClipboardDocumentIcon, CursorArrowRaysIcon, TruckIcon } from '@heroicons/react/24/outline'
import { Card, CardBody, Spinner } from '@nextui-org/react'
import HighchartsReact from 'highcharts-react-official'
import React, { useState, useMemo } from 'react'
import { getFetcher, HIGHCHART_URL } from '../../../../../services/api';
import useSWR from 'swr';
import Highcharts from "highcharts";
import StatWidget from "../../../../ds/StatWidget";

export default function OverviewChart() {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const {data, isLoading} = useSWR(`/api/dashboard/admin/overview?startMonth=${selectedMonth}&startYear=${selectedYear}`, getFetcher)
    const {data: dataRegistrasiOverview, isLoading:isLoadingRegistrasiOverview} = useSWR(`/api/dashboard/admin/registrasi-overview`, getFetcher)

    const {data: dataTipeRegistrasi, isLoading: isLoadingTipeRegistrasi} = useSWR('/api/dashboard/admin/registrasi-per-jenis', getFetcher)
    const { data:dataRegistrasiPerusahaan, isLoading: isLoadingRegistrasiPerusahaan } = useSWR(`/api/dashboard/admin/permohonan-registrasi-perusahaan?tahun=${selectedYear}`, getFetcher);

    const {data: dataRekomendasiOverview, isLoading: isLoadingRekomendasiOverview} = useSWR('/api/dashboard/admin/rekomendasi-overview', getFetcher)
    const {data: dataTipeRekomendasi, isLoading: isLoadingTipeRekomendasi} = useSWR('/api/dashboard/admin/rekomendasi-per-tipe-surat', getFetcher)
    const {data: dataRekomendasiPerusahaan, isLoading: isLoadingRekomendasiPerusahaan} = useSWR(`/api/dashboard/admin/permohonan-rekomendasi-perusahaan?tahun=${selectedYear}`, getFetcher)

    // Fungsi helper untuk transformasi data dari API
    const transformData = (data = [], fields = []) => {
        return fields.reduce((acc, field) => {
            acc[field] = data.map((item) => item[field]);
            return acc;
        }, {});
    };

    const processData = (data) => {
        return data.map(item => item === undefined ? 0 : item); // Ganti undefined dengan 0
    };
    

    // Data untuk Chart Registrasi (Memoization untuk optimalisasi)
    const registrasiChartData = useMemo(() => {
        const prosesRegistrasi = dataRegistrasiOverview?.data?.proses_registrasi || [];
        return transformData(prosesRegistrasi, ["tahun", "permohonan", "selesai"]);
    }, [dataRegistrasiOverview]);

    // Data untuk Chart Registrasi Per Jenis
    const tipeRegistrasiChartData = useMemo(() => {
        const prosesTipeRegistrasi = dataTipeRegistrasi?.data?.proses_registrasi || [];
        return transformData(prosesTipeRegistrasi, ["tahun", "permohonan", "selesai", "import", "produsen"]);
    }, [dataTipeRegistrasi]);

    // Data untuk Chart Rekomendasi (Memoization untuk optimalisasi)
    const rekomendasiChartData = useMemo(() => {
        const prosesRekomendasi = dataRekomendasiOverview?.data?.proses_aplikasi || [];
        return transformData(prosesRekomendasi, ["tahun", "permohonan", "selesai"]);
    }, [dataRekomendasiOverview]);

    // Data untuk Chart Rekomendasi Per Jenis
    const tipeRekomendasiChartData = useMemo(() => {
        const prosesTipeRekomendasi = dataTipeRekomendasi?.data?.proses_aplikasi || [];
        return transformData(prosesTipeRekomendasi, ["tahun", "permohonan", "Permohonan Baru", "Penambahan Jenis B3", "Penambahan Unit Kendaraan", "Perpanjangan"]);
    }, [dataTipeRekomendasi]);
    
    // Konfigurasi Highcharts Registrasi
    const registrasiChartOptions = {
        chart: { type: "column" },
        title: { text: "Grafik Registrasi dan Penyelesaian Permohonan" },
        xAxis: { categories: registrasiChartData.tahun },
        yAxis: { title: { text: "Jumlah" } },
        series: [
            { name: "Permohonan", data: registrasiChartData.permohonan },
            { name: "Selesai", data: registrasiChartData.selesai }
        ],
        exporting: { url: HIGHCHART_URL, enabled: true, showTable: true }
    };

    const barChartOptions = {
        chart: {
            type: "column", // Ganti "bar" menjadi "column"
        },
        title: {
            text: "Grafik Pelaporan B3",
        },
        xAxis: {
            categories: data?.data?.grafikPelaporan?.map((item) => item.nama_bahan_kimia),
        },
        yAxis: {
            title: {
                text: "Jumlah",
            },
        },
        series: [
            {
                name: "Pembelian Lokal",
                data: data?.data?.grafikPelaporan?.map((item) => Number(item.pembelian_lokal)|| 0) ?? [],
            },
            {
                name: "Pembelian Impor",
                data: data?.data?.grafikPelaporan?.map((item) => Number(item.pembelian_impor)|| 0)?? [],
            },
            {
                name: "Produksi",
                data: data?.data?.grafikPelaporan?.map((item) => Number(item.produksi)|| 0) ?? [],
            },
            {
                name: "Penggunaan",
                data: data?.data?.grafikPelaporan?.map((item) => Number(item.penggunaan)|| 0) ?? [],
            },
            {
                name: "Distribusi",
                data: data?.data?.grafikPelaporan?.map((item) =>  Number(item.distribusi)|| 0) ?? [],
            },
        ],
        exporting: {
            url: HIGHCHART_URL,
            enabled: true,
            showTable: true,
        },
    };

    const pieChartOptions = {
        chart: {
            type: "pie",
        },
        title: {
            text: "Grafik Registrasi B3",
        },
        series: [
            {
                name: "Registrasi",
                colorByPoint: true,
                data: data?.data?.grafikRegistrasi?.map((item) => ({
                    name: item.nama_bahan_kimia,
                    y: Number(item.total_registrasi)|| 0,
                })) ?? [],
            },
        ],
        exporting: {
            url: HIGHCHART_URL,
            enabled: true,
        },
    };

    // Konfigurasi Highcharts Registrasi Per Jenis
    const registrationTypeChartOptions = {
        chart: { type: "column" },
        title: { text: "Grafik Registrasi B3 Per Tipe Pengajuan" },
        xAxis: { categories: tipeRegistrasiChartData?.tahun },
        yAxis: { title: { text: "Jumlah" } },
        series: [
            { name: "Permohonan", data: tipeRegistrasiChartData?.permohonan },
            { name: "Selesai", data: tipeRegistrasiChartData?.selesai },
            { name: "Import", data: tipeRegistrasiChartData?.import },
            { name: "Produsen", data: tipeRegistrasiChartData?.produsen }
        ],
        exporting: { url: HIGHCHART_URL, enabled: true, showTable: true }
    };

    // Konfigurasi Chart untuk Permohonan Registrasi Perusahaan
    const permohonanRegistrasiChartOptions = {
        chart: { type: 'column' },
        title: { text: `Grafik Permohonan Registrasi Perusahaan - Tahun ${selectedYear}` },
        xAxis: { categories: dataRegistrasiPerusahaan?.data?.data?.map((item) => item.nama_perusahaan) },
        yAxis: { title: { text: 'Jumlah Permohonan' } },
        series: [
            {
                name: 'Permohonan',
                data: dataRegistrasiPerusahaan?.data?.data?.map((item) => item.permohonan),
            },
        ],
        exporting: { url: HIGHCHART_URL, enabled: true, showTable: true }
    };
    
    const recomendationChartOptions = {
        chart: { type: "column" },
        title: { text: "Grafik Rekomendasi dan Penyelesaian Permohonan" },
        xAxis: { categories: rekomendasiChartData.tahun },
        yAxis: { title: { text: "Jumlah" } },
        series: [
            { name: "Permohonan", data: rekomendasiChartData?.permohonan },
            { name: "Selesai", data: rekomendasiChartData?.selesai }
        ],
        exporting: { url: HIGHCHART_URL, enabled: true, showTable: true }
    };

        // Konfigurasi Highcharts Registrasi Per Jenis
        const rekomendasiTypeChartOptions = {
            chart: { type: "column" },
            title: { text: "Grafik Rekomendasi Pengangkutan B3 Per Tipe Pengajuan" },
            xAxis: { categories: tipeRekomendasiChartData.tahun },
            yAxis: { title: { text: "Jumlah" } },
            series: [
                { name: "Permohonan", data: processData(tipeRekomendasiChartData?.permohonan) },
                { name: "Permohonan Baru", data: processData(tipeRekomendasiChartData?.["Permohonan Baru"]) },
                { name: "Penambahan Jenis B3", data: processData(tipeRekomendasiChartData?.["Penambahan Jenis B3"]) },
                { name: "Penambahan Unit Kendaraan", data: processData(tipeRekomendasiChartData?.["Penambahan Unit Kendaraan"]) },
                { name: "Perpanjangan", data: processData(tipeRekomendasiChartData?.Perpanjangan) }
            ],
            exporting: { url: HIGHCHART_URL, enabled: true, showTable: true }
        };

    const companyRecomendationChartOptions = {
        chart: {
            type: "column",
        },
        title: {
            text: `Grafik Rekomendasi Pengangkutan B3 Per Perusahaan - Tahun ${selectedYear}` 
        },
        xAxis: { categories: dataRekomendasiPerusahaan?.data?.data?.map((item) => item.nama_perusahaan) },
        yAxis: { title: { text: 'Jumlah Permohonan' } },
        series: [
            {
                name: 'Permohonan',
                data: dataRekomendasiPerusahaan?.data?.data?.map((item) => item.permohonan),
            },
        ],
        exporting: { url: HIGHCHART_URL, enabled: true, showTable: true }
    };

    return (
        <div>
            {
                isLoading 
                    ? 
                    <div className='w-full flex justify-center items-center'>
                        <Spinner size="lg" color="primary" />
                    </div>
                    :
                    <>
                        {/* KPI — kartu .widget design system; tiga blok identik
                            sebelumnya diganti satu definisi data agar tidak terduplikasi. */}
                        <div className="grid grid-cols-1 gap-[18px] md:grid-cols-2 lg:grid-cols-3">
                            {
                                [
                                    {
                                        caption: "Registrasi B3",
                                        value: `${data?.data?.registrasiCount ?? 0} Pengajuan`,
                                        change: data?.data?.registrasiChange,
                                        icon: <ClipboardDocumentIcon className="size-5" />,
                                        valueClassName: "text-g-600",
                                        iconClassName: "bg-g-600",
                                    },
                                    {
                                        caption: "Rekomendasi Pengangkutan B3",
                                        value: `${data?.data?.rekomendasiCount ?? 0} Pengajuan`,
                                        change: data?.data?.rekomendasiChange,
                                        icon: <CursorArrowRaysIcon className="size-5" />,
                                        valueClassName: "text-b-600",
                                        iconClassName: "bg-b-600",
                                    },
                                    {
                                        caption: "Realisasi Impor B3",
                                        value: `${data?.data?.realisasiImpor ?? 0} Kg`,
                                        change: data?.data?.realisasiImporChange,
                                        icon: <TruckIcon className="size-5" />,
                                        valueClassName: "text-o-700",
                                        iconClassName: "bg-o-700",
                                    },
                                ].map((kpi) => {
                                    const naik = (kpi.change ?? 0) >= 0

                                    return (
                                        <StatWidget
                                            key={kpi.caption}
                                            className="min-w-0"
                                            value={kpi.value}
                                            caption={kpi.caption}
                                            icon={kpi.icon}
                                            iconClassName={kpi.iconClassName}
                                            valueClassName={kpi.valueClassName}
                                            trendDirection={naik ? "up" : "down"}
                                            trend={
                                                <>
                                                    {
                                                        naik
                                                            ? <ArrowTrendingUpIcon className="size-3" />
                                                            : <ArrowTrendingDownIcon className="size-3" />
                                                    }
                                                    <span>{kpi.change?.toFixed(2) ?? 0}% dari bulan lalu</span>
                                                </>
                                            }
                                        />
                                    )
                                })
                            }
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
                            <Card radius='sm'>
                                <CardBody>
                                {
                                    data && data.data?.grafikPelaporan
                                        ?
                                        <HighchartsReact highcharts={Highcharts} options={barChartOptions} />
                                        : 
                                        <p>Loading chart...</p>
                                }
                                </CardBody>
                            </Card>        
                            <Card radius='sm'>
                                <CardBody>
                                {
                                    data && data.data?.grafikRegistrasi 
                                        ?
                                        <HighchartsReact highcharts={Highcharts} options={pieChartOptions} />  
                                        : 
                                        <p>Loading chart...</p>
                                }
                                </CardBody>
                            </Card>
                            <Card radius='sm'>
                                <CardBody>
                                {
                                    <HighchartsReact highcharts={Highcharts} options={registrasiChartOptions} />
                                }
                                </CardBody>
                            </Card>  
                            <Card radius='sm'>
                                <CardBody>
                                {
                                    <HighchartsReact highcharts={Highcharts} options={registrationTypeChartOptions} />
                                }
                                </CardBody>
                            </Card>
                            <Card radius='sm'>
                                <CardBody>
                                {
                                    <HighchartsReact highcharts={Highcharts} options={permohonanRegistrasiChartOptions} />
                                }
                                </CardBody>
                            </Card>                                                           
                            <Card radius='sm'>
                                <CardBody>
                                {
                                    <HighchartsReact highcharts={Highcharts} options={recomendationChartOptions} />
                                }
                                </CardBody>
                            </Card>
                            <Card radius='sm'>
                                <CardBody>
                                {
                                    <HighchartsReact highcharts={Highcharts} options={rekomendasiTypeChartOptions} />
                                }
                                </CardBody>
                            </Card>                                              
                            <Card radius='sm'>
                                <CardBody>
                                {
                                    <HighchartsReact highcharts={Highcharts} options={companyRecomendationChartOptions} />
                                }
                                </CardBody>
                            </Card>                                                          
                        </div>
                    </>
            }
        </div>
    )
}
